import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faPlus,
  faPen,
  faTrash,
  faSpinner,
  faLayerGroup,
  faSearch,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";

import {
  getAllCustomerTags,
  getCustomerTagUsage,
  createCustomerTag,
  updateCustomerTag,
  deleteCustomerTag,
  mergeCustomerTags,
} from "../../../../api/crm/customers/customerTagInfo";

import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../../utils/confirmDelete";

import MergeTagDialog from "./MergeTagDialog";

// 🎨 COLOR PRESET
const COLOR_PRESET = [
  "#1E88E5",
  "#43A047",
  "#FB8C00",
  "#E53935",
  "#8E24AA",
  "#6D4C41",
  "#546E7A",
  "#FDD835",
];

const CustomerTagsModal = ({ isOpen, onClose }) => {
  const [tags, setTags] = useState([]);
  const [usage, setUsage] = useState([]); // số KH đang dùng tag
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState("");

  // FORM ADD / EDIT
  const [editingTag, setEditingTag] = useState(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState("#E53935");

  // MULTI SELECT MERGE
  const [selectedTags, setSelectedTags] = useState([]);
  const [showMergeDialog, setShowMergeDialog] = useState(false);

  // Load tags + usage count
  const loadData = async () => {
    try {
      setLoading(true);
      const res = await getAllCustomerTags();
      const usageData = await getCustomerTagUsage();

      setTags(res.items || []);
      setUsage(usageData || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen]);

  const resetForm = () => {
    setEditingTag(null);
    setName("");
    setColor("#E53935");
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    try {
      setSaving(true);

      if (editingTag) {
        await updateCustomerTag(editingTag.id, { name, color });
        showSuccessToast("Cập nhật tag thành công");
      } else {
        await createCustomerTag({ name, color });
        showSuccessToast("Thêm tag mới thành công");
      }

      resetForm();
      await loadData();
    } catch (err) {
      showErrorToast("Lưu tag thất bại");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag) => {
    const id = typeof tag === "object" ? tag?.id : tag;
    const name = typeof tag === "object" ? tag?.name : undefined;
    const result = await confirmDelete({
      type: DELETE_ENTITY.customer_tag,
      name,
    });

    if (!result.isConfirmed) return;

    try {
      await deleteCustomerTag(id);
      showSuccessToast("Đã xoá tag");
      await loadData();
    } catch (err) {
      showErrorToast("Xóa tag thất bại");
    }
  };

  const toggleSelectTag = (id) => {
    setSelectedTags((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const getUsageCount = (tagId) => {
    const found = usage.find((u) => u.tag_id === tagId);
    return found ? found.usage_count : 0;
  };

  const filteredTags = tags.filter((t) =>
    t.name.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  return (
    <>
      {/* MERGE POPUP */}
      <MergeTagDialog
        isOpen={showMergeDialog}
        onClose={() => setShowMergeDialog(false)}
        oldTagIds={selectedTags}
        onMerged={async () => {
          setSelectedTags([]);
          await loadData();
        }}
      />

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[999]"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              className="bg-white w-[580px] max-w-[95%] rounded-2xl shadow-2xl p-6"
            >
              {/* HEADER */}
              <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h2 className="text-xl font-semibold">Quản lý Tags khách hàng</h2>
                <button onClick={onClose}>
                  <FontAwesomeIcon
                    icon={faClose}
                    className="text-gray-500 text-lg hover:text-gray-700"
                  />
                </button>
              </div>

              {/* SEARCH BAR */}
              <div className="flex items-center gap-2 mb-4 p-2 border rounded-xl bg-gray-50">
                <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                <input
                  type="text"
                  placeholder="Tìm kiếm tag..."
                  className="flex-1 bg-transparent focus:outline-none"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </div>

              {/* MERGE BUTTON */}
              {selectedTags.length >= 2 && (
                <button
                  onClick={() => setShowMergeDialog(true)}
                  className="mb-4 flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                >
                  <FontAwesomeIcon icon={faLayerGroup} />
                  Gộp {selectedTags.length} tags
                </button>
              )}

              {/* TAG LIST */}
              <div className="space-y-3 max-h-[320px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {loading && (
                  <p className="text-gray-500 text-center py-3 animate-pulse">
                    Đang tải danh sách...
                  </p>
                )}

                {!loading &&
                  filteredTags.map((t) => {
                    const isEditing = editingTag?.id === t.id;
                    const isSelected = selectedTags.includes(t.id);

                    return (
                      <div
                        key={t.id}
                        className={`flex items-center justify-between p-3 rounded-xl border shadow-sm transition cursor-pointer
                          ${
                            isEditing
                              ? "bg-blue-50 border-blue-300"
                              : isSelected
                              ? "bg-green-50 border-green-400"
                              : "bg-gray-50 hover:bg-gray-100"
                          }
                        `}
                      >
                        {/* LEFT AREA → select + name */}
                        <div className="flex items-center gap-3">
                          {/* SELECT */}
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectTag(t.id)}
                            className="w-4 h-4"
                          />

                          {/* COLOR DOT */}
                          <span
                            className="w-4 h-4 rounded-full inline-block shadow-sm"
                            style={{ backgroundColor: t.color }}
                          ></span>

                          <span className="font-medium select-none">{t.name}</span>

                          {/* USAGE COUNT */}
                          <span className="text-xs text-gray-500">
                            {getUsageCount(t.id)}
                          </span>
                        </div>

                        {/* RIGHT AREA → buttons */}
                        <div className="flex items-center gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-800"
                            onClick={() => {
                              setEditingTag(t);
                              setName(t.name);
                              setColor(t.color);
                            }}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </button>

                          <button
                            className="text-red-600 hover:text-red-800"
                            onClick={() => handleDelete(t)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
              </div>

              {/* FORM ADD / EDIT */}
              <div className="mt-5 p-5 border rounded-xl bg-gray-50 space-y-4 shadow-inner">
                <div>
                  <label className="block text-sm font-medium mb-1">Tên tag</label>
                  <input
                    type="text"
                    className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Nhập tên tag..."
                  />
                </div>

                {/* COLOR PRESET */}
                <div>
                  <label className="block text-sm font-medium mb-1">Màu sắc</label>
                  <div className="flex gap-2 mb-2">
                    {COLOR_PRESET.map((c) => (
                      <div
                        key={c}
                        onClick={() => setColor(c)}
                        className={`w-7 h-7 rounded-full cursor-pointer border
                          ${
                            color === c
                              ? "ring-2 ring-blue-500 scale-110"
                              : "hover:scale-105"
                          }
                          transition shadow-sm
                        `}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>

                  <input
                    type="color"
                    className="w-16 h-10 border rounded cursor-pointer shadow-sm"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                  />
                </div>

                {/* SAVE BUTTON */}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`w-full py-2 rounded-lg 
                    bg-gradient-to-r from-red-600 to-red-800 
                    text-white font-semibold transition-transform
                    ${
                      saving
                        ? "opacity-60 cursor-not-allowed"
                        : "hover:scale-[1.02]"
                    }
                  `}
                >
                  {saving ? (
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  )}
                  {editingTag ? "Cập nhật tag" : "Thêm tag mới"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomerTagsModal;
